import { jsx as _jsx } from "react/jsx-runtime";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import EditableToggle from './EditableToggle';
const messages = defineMessages({
    InvalidPhoneNumber: {
        id: 'msgInvalidPhoneNumber',
        defaultMessage: 'Please enter a valid phone number',
    },
});
const UTILS_URL = 'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.0/js/utils.js';
const EditablePhoneInput = ({ value, onChange, country, readOnly = false, placeholder = '', onError, }) => {
    const intl = useIntl();
    const itiRef = useRef(null);
    const inputElRef = useRef(null);
    const snapshotRef = useRef({ number: '', valid: true });
    const intendedCountryRef = useRef((country === null || country === void 0 ? void 0 : country.toLowerCase()) || '');
    const fromDropdownRef = useRef(false);
    const submitRef = useRef(() => { });
    const [hasError, setHasError] = useState(false);
    const onErrorRef = useRef(onError);
    onErrorRef.current = onError;
    const intlRef = useRef(intl);
    intlRef.current = intl;
    const setHasErrorRef = useRef(setHasError);
    setHasErrorRef.current = setHasError;
    useEffect(() => {
        return () => {
            if (itiRef.current) {
                itiRef.current.destroy();
                itiRef.current = null;
            }
        };
    }, []);
    useEffect(() => {
        if (itiRef.current && country) {
            const code = country.toLowerCase();
            intendedCountryRef.current = code;
            itiRef.current.setCountry(code);
        }
    }, [country]);
    const captureSnapshot = useCallback(() => {
        if (itiRef.current) {
            snapshotRef.current = {
                number: itiRef.current.getNumber(1) || '',
                valid: itiRef.current.isValidNumber(),
            };
        }
    }, []);
    const showValidationError = useCallback(() => {
        onError === null || onError === void 0 ? void 0 : onError(intl.formatMessage(messages.InvalidPhoneNumber));
        setHasError(true);
    }, [onError, intl]);
    const validateAndSubmit = useCallback((submit) => {
        captureSnapshot();
        const { number, valid } = snapshotRef.current;
        if (!number) {
            setHasError(false);
            submit();
            return;
        }
        if (!valid) {
            showValidationError();
            return;
        }
        setHasError(false);
        submit();
    }, [captureSnapshot, showValidationError]);
    const handleSubmit = useCallback((_editValue) => {
        const { number } = snapshotRef.current;
        const currentNumber = number || _editValue;
        if (!currentNumber) {
            return onChange('');
        }
        return onChange(currentNumber);
    }, [onChange]);
    const initIti = useCallback((el, currentValue) => {
        var _a;
        const intlTelInput = window.intlTelInput;
        if (!intlTelInput)
            return;
        itiRef.current = intlTelInput(el, {
            nationalMode: true,
            autoPlaceholder: 'aggressive',
            initialCountry: intendedCountryRef.current || (country === null || country === void 0 ? void 0 : country.toLowerCase()) || '',
            preferredCountries: ['be', 'nl', 'fr', 'lu', 'es'],
            utilsScript: UTILS_URL,
        });
        const numberToRestore = snapshotRef.current.number || currentValue;
        if (numberToRestore) {
            itiRef.current.setNumber(numberToRestore);
            intendedCountryRef.current =
                ((_a = itiRef.current.getSelectedCountryData()) === null || _a === void 0 ? void 0 : _a.iso2) || intendedCountryRef.current;
            snapshotRef.current = {
                number: snapshotRef.current.number || numberToRestore,
                valid: snapshotRef.current.valid,
            };
        }
        else if (country) {
            itiRef.current.setCountry(country.toLowerCase());
        }
        el.addEventListener('open:countrydropdown', () => {
            fromDropdownRef.current = true;
        });
        el.addEventListener('close:countrydropdown', () => {
            setTimeout(() => {
                fromDropdownRef.current = false;
            }, 200);
        });
        el.addEventListener('countrychange', () => {
            var _a, _b;
            if (!itiRef.current)
                return;
            if (fromDropdownRef.current) {
                intendedCountryRef.current =
                    ((_a = itiRef.current.getSelectedCountryData()) === null || _a === void 0 ? void 0 : _a.iso2) || intendedCountryRef.current;
                setTimeout(() => {
                    var _a;
                    captureSnapshot();
                    if (snapshotRef.current.number && !snapshotRef.current.valid) {
                        (_a = onErrorRef.current) === null || _a === void 0 ? void 0 : _a.call(onErrorRef, intlRef.current.formatMessage(messages.InvalidPhoneNumber));
                        setHasErrorRef.current(true);
                    }
                    else {
                        setHasErrorRef.current(false);
                        submitRef.current();
                    }
                }, 100);
            }
            else if (intendedCountryRef.current) {
                const current = (_b = itiRef.current.getSelectedCountryData()) === null || _b === void 0 ? void 0 : _b.iso2;
                if (current !== intendedCountryRef.current) {
                    itiRef.current.setCountry(intendedCountryRef.current);
                }
            }
        });
    }, [country]);
    return (_jsx(EditableToggle, Object.assign({ onSubmit: handleSubmit, value: value, readOnly: readOnly, onModeChange: (mode) => {
            if (mode === 'display') {
                if (itiRef.current) {
                    itiRef.current.destroy();
                    itiRef.current = null;
                }
            }
        } }, { children: ({ inputValue, handleChange, inputRef: toggleInputRef, submit, cancel, handleKeyDown, }) => {
            submitRef.current = submit;
            return (_jsx("div", Object.assign({ className: hasError ? 'has-error' : undefined }, { children: _jsx("input", { onChange: e => {
                        handleChange(e);
                        captureSnapshot();
                        setHasError(false);
                    }, ref: (el) => {
                        toggleInputRef(el);
                        if (!el)
                            return;
                        if (el === inputElRef.current)
                            return;
                        if (itiRef.current) {
                            itiRef.current.destroy();
                            itiRef.current = null;
                        }
                        inputElRef.current = el;
                        initIti(el, inputValue);
                    }, onBlur: () => {
                        captureSnapshot();
                        setTimeout(() => {
                            var _a;
                            if (fromDropdownRef.current)
                                return;
                            const itiContainer = (_a = inputElRef.current) === null || _a === void 0 ? void 0 : _a.closest('.iti');
                            const stillInIti = itiContainer && itiContainer.contains(document.activeElement);
                            if (!stillInIti && document.activeElement !== inputElRef.current) {
                                validateAndSubmit(submit);
                            }
                        }, 100);
                    }, onKeyDown: (e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            validateAndSubmit(submit);
                            return;
                        }
                        if (e.key === 'Escape') {
                            setHasError(false);
                            handleKeyDown(e);
                            return;
                        }
                        handleKeyDown(e);
                    }, className: "form-control", placeholder: placeholder, autoComplete: "off", type: "tel" }) })));
        } })));
};
export default EditablePhoneInput;
